import { ArrowRight, BookOpen, ClipboardList, Wallet } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { formatDate, formatMoney, formatMonthShort, formatNumber, humanize } from '@/utils/format'
import type { DashboardData } from '../api'
import { ChartTooltip } from './ChartTooltip'

function Figure({ label, value, tone }: { label: string; value: string; tone?: 'danger' }) {
  return (
    <div className="min-w-0">
      <p className="text-xs text-muted">{label}</p>
      <p
        className={`mt-0.5 truncate text-lg font-semibold tabular ${tone === 'danger' ? 'text-danger-600' : 'text-ink'}`}
      >
        {value}
      </p>
    </div>
  )
}

export function FinanceCard({
  data,
  currency,
}: {
  data: NonNullable<DashboardData['finance']>
  currency: string
}) {
  const { t } = useTranslation('dashboard')
  const money = (v: string | number) => formatMoney(v, currency)
  const points = data.collectionsByMonth.map((d) => ({ ...d, label: formatMonthShort(d.month) }))
  return (
    <Card>
      <CardHeader
        title={t('Fees')}
        description={t('Collections and outstanding balances')}
        icon={<Wallet />}
        actions={
          <Button asChild variant="ghost" size="sm" rightIcon={<ArrowRight />}>
            <Link to="/admin/finance/reports">{t('Reports')}</Link>
          </Button>
        }
      />
      <CardBody>
        <div className="grid grid-cols-3 gap-4">
          <Figure label={t('Collected this month')} value={money(data.collectedThisMonth)} />
          <Figure label={t('Outstanding')} value={money(data.outstandingTotal)} />
          <Figure
            label={t('Overdue ({{value}})', { value: formatNumber(data.overdueCount) })}
            value={money(data.overdueTotal)}
            tone={data.overdueCount ? 'danger' : undefined}
          />
        </div>
        <div
          className="mt-4 h-28"
          role="img"
          aria-label={t('Fee collections per month over the last 12 months')}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={points} margin={{ top: 4, right: 0, left: 0, bottom: 0 }} barCategoryGap="22%">
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 10, fill: 'var(--muted)' }}
                interval={1}
              />
              <Tooltip
                cursor={{ fill: 'var(--surface-sunken)' }}
                content={({ active, payload, label }) => (
                  <ChartTooltip
                    active={active}
                    label={label}
                    payload={payload.map((p) => ({ value: money(Number(p.value)), color: '#4f46e5' }))}
                  />
                )}
              />
              <Bar dataKey="value" fill="#4f46e5" radius={[3, 3, 0, 0]} maxBarSize={22} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardBody>
    </Card>
  )
}

export function UpcomingExamsCard({ exams }: { exams: NonNullable<DashboardData['upcomingExams']> }) {
  const { t } = useTranslation('dashboard')
  return (
    <Card>
      <CardHeader
        title={t('Examinations')}
        description={t('Scheduled and open for marks')}
        icon={<ClipboardList />}
        actions={
          <Button asChild variant="ghost" size="sm" rightIcon={<ArrowRight />}>
            <Link to="/admin/exams">{t('All exams')}</Link>
          </Button>
        }
      />
      {exams.length === 0 ? (
        <CardBody>
          <p className="text-sm text-muted">{t('No upcoming exams.')}</p>
        </CardBody>
      ) : (
        <ul className="divide-y divide-line">
          {exams.map((e) => {
            const pct = e.marksExpected ? Math.round((e.marksEntered / e.marksExpected) * 100) : 0
            return (
              <li key={e.id}>
                <Link
                  to={`/admin/exams/${e.id}`}
                  className="block px-5 py-3.5 transition-colors hover:bg-surface-muted"
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="truncate text-sm font-medium text-ink">{e.name}</p>
                    <Badge tone={e.status === 'marks_open' ? 'warning' : 'info'} size="sm">
                      {humanize(e.status)}
                    </Badge>
                  </div>
                  <p className="mt-0.5 text-xs text-muted">
                    {formatDate(e.startsOn)} – {formatDate(e.endsOn)} ·{' '}
                    {t('{{value}} classes', { count: e.classCount, value: formatNumber(e.classCount) })}
                  </p>
                  {e.status === 'marks_open' ? (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="h-1.5 flex-1 rounded-full bg-surface-sunken" aria-hidden="true">
                        <div className="h-full rounded-full bg-warning-500" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-2xs text-muted tabular">
                        {t('{{pct}}% marks entered', { pct })}
                      </span>
                    </div>
                  ) : null}
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}

export function LibraryCard({
  data,
  currency,
}: {
  data: NonNullable<DashboardData['library']>
  currency: string
}) {
  const { t } = useTranslation('dashboard')
  return (
    <Card>
      <CardHeader
        title={t('Library')}
        icon={<BookOpen />}
        actions={
          <Button asChild variant="ghost" size="sm" rightIcon={<ArrowRight />}>
            <Link to="/admin/library/loans">{t('Loans')}</Link>
          </Button>
        }
      />
      <CardBody className="grid grid-cols-2 gap-4">
        <Figure label={t('Titles')} value={formatNumber(data.titles)} />
        <Figure label={t('On loan')} value={`${formatNumber(data.onLoan)} / ${formatNumber(data.copies)}`} />
        <Figure
          label={t('Overdue')}
          value={formatNumber(data.overdue)}
          tone={data.overdue ? 'danger' : undefined}
        />
        <Figure label={t('Unpaid fines')} value={formatMoney(data.finesOutstanding, currency)} />
      </CardBody>
    </Card>
  )
}
